import React from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { theme } from "./theme";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { TelemetryProvider } from "./contexts/TelemetryContext";
import { FeatureRequestProvider } from "./contexts/FeatureRequestContext";
import { RecipeProvider } from "./contexts/RecipeContext";
import { UsageEventProvider } from "./contexts/UsageEventContext";

import ProtectedRoute from "./components/ProtectedRoute";
import { LegacyRedirect } from "./utils/LegacyRedirect";

import { ROUTES, LEGACY_ROUTES } from "./constants/routes";

import MateriomAI from "./pages/MateriomAI";
import AuthenticatedMateriomAI from "./pages/AuthenticatedMateriomAI";
import Dashboard from "./pages/Dashboard";
import OnRegistrationSurvey from "./pages/OnRegistrationSurvey";
import MaterialsDatabase from "./pages/MaterialsDatabase";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ContributionPortal from "./pages/ContributionPortal";
import UserProfile from "./pages/UserProfile";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import UserDirectory from "./pages/UserDirectory";
import Recipe from "./pages/Recipe";
import UpdateUser from "./pages/UpdateDetails";
import Licenses from "./pages/Licenses";
import { ProfileUpdateProvider } from "./contexts/ProfileUpdateContext";

/**
 * Main application component for the Materiom AI
 *
 * Sets up routing with authentication using centralized route constants.
 * Route paths are now managed in constants/routes.ts for better maintainability.
 *
 * The AuthProvider wraps the entire app to provide authentication context.
 * The TelemetryProvider handles PostHog integration and user identification.
 */
function App() {
  return (
    <AuthProvider>
      <TelemetryProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <UsageEventProvider>
              <FeatureRequestProvider>
                <Routes>
                  {/* Redirect old Commons paths to new paths for backward compatibility */}
                  <Route
                    path={`${LEGACY_ROUTES.DATA_COMMONS}/*`}
                    element={
                      <LegacyRedirect
                        from={LEGACY_ROUTES.DATA_COMMONS}
                        to={ROUTES.MATERIALS_DATABASE}
                      />
                    }
                  />

                  {/* Public routes */}
                  <Route path={ROUTES.LOGIN} element={<Login />} />
                  <Route
                    path={ROUTES.FORGOT_PASSWORD}
                    element={<ForgotPassword />}
                  />
                  <Route
                    path={ROUTES.RESET_PASSWORD}
                    element={<ResetPassword />}
                  />
                  <Route path={ROUTES.REGISTER} element={<Register />} />
                  <Route
                    path={ROUTES.EMAIL_VERIFICATION}
                    element={<VerifyEmail />}
                  />
                  <Route
                    path={`${ROUTES.EMAIL_VERIFICATION}/:userId/:hash`}
                    element={<VerifyEmail />}
                  />
                  <Route
                    path={ROUTES.REGISTRATION_SURVEY}
                    element={<OnRegistrationSurvey />}
                  />
                  <Route
                    path={`${ROUTES.USER_DIRECTORY}/:userId`}
                    element={<UserProfile />}
                  />
                  {/* Protected routes */}
                  <Route
                    path={ROUTES.ROOT}
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  {/* Redirect old MVP Materiom AI route to new route */}
                  {/* TODO: Remove this route once we fully deprecate the MVP frontend */}
                  <Route
                    path={ROUTES.LEGACY_MATERIOM_AI}
                    element={
                      <ProtectedRoute>
                        <MateriomAI />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.DASHBOARD}
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.MATERIOM_AI}
                    element={
                      <ProtectedRoute>
                        <AuthenticatedMateriomAI />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.CONTRIBUTION_PORTAL}
                    element={
                      <ProtectedRoute>
                        <ContributionPortal />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path={ROUTES.USER_DIRECTORY}
                    element={
                      <ProtectedRoute>
                        <UserDirectory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.MATERIALS_DATABASE}
                    element={
                      <ProtectedRoute>
                        <MaterialsDatabase />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.UPDATE_DETAILS}
                    element={
                      // This is different from the other Route with a provider, the pattern is inconsistent
                      // because of ProfileUpdateProvider need to be authenticated.
                      <ProtectedRoute>
                        <ProfileUpdateProvider>
                          <UpdateUser />
                        </ProfileUpdateProvider>
                      </ProtectedRoute>
                    }
                  />
                  {/* Only wrap the recipe detail route with RecipeProvider */}
                  <Route
                    path={`${ROUTES.RECIPE_DETAIL}/:recipeId`}
                    element={
                      <RecipeProvider>
                        <Recipe />
                      </RecipeProvider>
                    }
                  />
                  <Route
                    path={ROUTES.LICENSES}
                    element={
                      <ProtectedRoute>
                        <Licenses />
                      </ProtectedRoute>
                    }
                  />
                  {/* A catch-all route for any undefined paths */}
                  <Route path="*" element={<p>Page Not Found</p>} />
                </Routes>
              </FeatureRequestProvider>
            </UsageEventProvider>
          </Router>
        </ThemeProvider>
      </TelemetryProvider>
    </AuthProvider>
  );
}

export default App;
